import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { IoHomeOutline } from 'react-icons/io5';
import { FaBolt } from "react-icons/fa";

// ==========================
// PowerSense Blog Content (8 Blogs)
// ==========================
const BlogContent = {
  1: {
    title: "Campus Energy Monitoring System (CEMS)",
    heroImage: "https://images.unsplash.com/photo-1509395176047-4a66953fd231",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "6 min read",
    tags: ["CEMS", "Energy", "IoT"],
    content: [
      { type: "paragraph", text: "Campus Energy Monitoring System (CEMS) helps track energy usage across buildings in real-time." },
      { type: "heading", text: "Why CEMS?" },
      { type: "paragraph", text: "It reduces energy waste and improves efficiency in large campuses." },
      { type: "heading", text: "Key Features" },
      { type: "list", items: ["Real-time monitoring", "Central dashboard", "Alerts & reports"] }
    ]
  },

  2: {
    title: "UPS Monitoring System",
    heroImage: "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "5 min read",
    tags: ["UPS", "Monitoring"],
    content: [
      { type: "paragraph", text: "UPS monitoring ensures uninterrupted power and system safety." },
      { type: "heading", text: "Benefits" },
      { type: "list", items: ["Battery health tracking", "Load monitoring", "Failure alerts"] }
    ]
  },

  3: {
    title: "Solar Monitoring System",
    heroImage: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "5 min read",
    tags: ["Solar", "Energy"],
    content: [
      { type: "paragraph", text: "Monitor solar panel output and efficiency in real-time." },
      { type: "heading", text: "Features" },
      { type: "list", items: ["Generation tracking", "Performance analytics", "Fault alerts"] }
    ]
  },

  4: {
    title: "Real-Time Energy Dashboard",
    heroImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "7 min read",
    tags: ["Dashboard", "Analytics"],
    content: [
      { type: "paragraph", text: "Visualize energy data using dashboards and widgets." },
      { type: "heading", text: "Advantages" },
      { type: "list", items: ["Live data", "Graphs & charts", "Easy insights"] }
    ]
  },

  5: {
    title: "Training & Courses Available",
    heroImage: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "4 min read",
    tags: ["Training", "Courses"],
    content: [
      { type: "paragraph", text: "Learn IoT and energy systems through our training programs." },
      { type: "heading", text: "Courses Include" },
      { type: "list", items: ["IoT Basics", "Energy Analytics", "Dashboard usage"] }
    ]
  },

  6: {
    title: "Agriculture Monitoring System",
    heroImage: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "6 min read",
    tags: ["Agriculture", "IoT"],
    content: [
      { type: "paragraph", text: "Smart agriculture monitoring improves crop productivity." },
      { type: "heading", text: "Benefits" },
      { type: "list", items: ["Soil monitoring", "Water tracking", "Automation"] }
    ]
  },

  7: {
    title: "Diesel Generator Monitoring",
    heroImage: "https://images.unsplash.com/photo-1581091870622-1e7c7a1c6d34",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "5 min read",
    tags: ["DG", "Monitoring"],
    content: [
      { type: "paragraph", text: "Monitor diesel generator performance and fuel usage." },
      { type: "heading", text: "Key Points" },
      { type: "list", items: ["Fuel tracking", "Efficiency monitoring", "Alerts"] }
    ]
  },

  8: {
    title: "Water Monitoring System",
    heroImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
    author: "PowerSense Team",
    date: "April 2026",
    readTime: "5 min read",
    tags: ["Water", "IoT"],
    content: [
      { type: "paragraph", text: "Monitor water usage and tank levels in real-time." },
      { type: "heading", text: "Features" },
      { type: "list", items: ["Level sensors", "Usage tracking", "Alerts"] }
    ]
  }
};

// ==========================
// Blog Page Component
// ==========================
const BlogPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const blog = BlogContent[id];

  if (!blog) return <div className="text-center py-20">Blog not found</div>;

  const recommendedBlogs = Object.entries(BlogContent)
    .filter(([blogId]) => blogId !== id)
    .map(([blogId, blogData]) => ({
      id: blogId,
      ...blogData
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-cyan-50 py-16">

      {/* Home Button */}
      <div className="fixed top-6 left-6">
        <Link to="/features" className="bg-white p-3 rounded-full shadow">
          <IoHomeOutline className="text-blue-600 text-xl" />
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4">

        {/* Main Blog */}
        <article className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg overflow-hidden">

          <img src={blog.heroImage} alt={blog.title} className="w-full h-[400px] object-cover" />

          <div className="p-8">
            <div className="text-sm text-gray-600 mb-4">
              {blog.author} | {blog.date} | {blog.readTime}
            </div>

            <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
              <FaBolt className="text-yellow-500" />
              {blog.title}
            </h1>

            {blog.content.map((section, i) => {
              if (section.type === "paragraph")
                return <p key={i} className="mb-4 text-gray-700">{section.text}</p>;

              if (section.type === "heading")
                return <h2 key={i} className="text-xl font-semibold mt-6 mb-3 text-blue-600">{section.text}</h2>;

              if (section.type === "list")
                return (
                  <ul key={i} className="list-disc pl-6 mb-4 text-gray-700">
                    {section.items.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                );
                return null;
            })}
            
          </div>
        </article>

        {/* Recommended */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Recommended Blogs</h2>

          <div className="grid md:grid-cols-3 gap-6">
            {recommendedBlogs.map((b) => (
              <div key={b.id} onClick={() => navigate(`/blog/${b.id}`)}
                className="bg-white/80 backdrop-blur-md rounded-xl shadow cursor-pointer hover:scale-105 transition">

                <img src={b.heroImage} alt={b.title} className="h-40 w-full object-cover" />

                <div className="p-4">
                  <h3 className="font-bold mb-2">{b.title}</h3>
                  <p className="text-sm text-gray-600">{b.content[0].text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BlogPage;